/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Admin API hooks for system administrators
 * Calls Python API endpoints at /api/admin/*
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import { useCallback, useEffect, useRef, useState } from "react";

// Get API base URL from environment
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const REST_API_BASE_URL = API_ENDPOINT.replace(/\/graphql$/, "");

// =====================================
// Admin API Types (Python Backend)
// =====================================

/** テナント情報（Admin用） */
export interface AdminTenant {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

/** テナント一覧レスポンス */
export interface AdminTenantListResponse {
  items: AdminTenant[];
  total: number;
}

/** テナントメンバー情報 */
export interface AdminTenantMember {
  userId: string;
  email: string;
  familyName: string;
  givenName: string;
  role: string;
}

/** テナント詳細レスポンス */
export interface AdminTenantDetailResponse {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  licenses: string[];
  members: AdminTenantMember[];
}

/** テナント更新リクエスト */
export interface UpdateTenantInput {
  name?: string;
  description?: string;
  isActive?: boolean;
}

/** ユーザー情報（Admin用） */
export interface AdminUser {
  id: string;
  cognitoSub: string;
  email: string;
  familyName: string;
  givenName: string;
  familyNameKana: string | null;
  givenNameKana: string | null;
  isSystemAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

/** ユーザー一覧レスポンス */
export interface AdminUserListResponse {
  items: AdminUser[];
  total: number;
}

/** ユーザー所属テナント情報 */
export interface AdminUserTenant {
  tenantId: string;
  tenantName: string;
  role: string;
}

/** ユーザー詳細レスポンス */
export interface AdminUserDetailResponse {
  id: string;
  cognitoSub: string;
  email: string;
  familyName: string;
  givenName: string;
  familyNameKana: string | null;
  givenNameKana: string | null;
  isSystemAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  tenants: AdminUserTenant[];
}

/** ユーザー更新リクエスト */
export interface UpdateUserInput {
  familyName?: string;
  givenName?: string;
  familyNameKana?: string;
  givenNameKana?: string;
}

/** システム管理者権限更新リクエスト */
export interface UpdateSystemAdminInput {
  isSystemAdmin: boolean;
}

// =====================================
// Generic fetch helper for admin endpoints
// =====================================

/**
 * 認証トークンを取得する
 * @return {Promise<string | null>} 認証トークン、取得できない場合はnull
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * Admin API呼び出しヘルパー関数
 * @param {string} method HTTPメソッド
 * @param {string} path APIパス
 * @param {unknown} body リクエストボディ
 * @return {Promise<T>} APIレスポンス
 */
async function fetchAdminApi<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Get auth token
  const token = await getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = {
    method,
    headers,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${REST_API_BASE_URL}${path}`, options);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { detail?: string }).detail ||
        `API error: ${res.status} ${res.statusText}`,
    );
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return {} as T;
  }

  return res.json();
}

// =====================================
// useAdminTenantsApi - テナント一覧取得
// =====================================

/** useAdminTenantsApiの戻り値 */
export interface UseAdminTenantsApiResult {
  data: AdminTenantListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminTenantListResponse>;
}

/**
 * テナント一覧を取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminTenantsApiResult} テナント一覧取得結果
 */
export function useAdminTenantsApi(): UseAdminTenantsApiResult {
  const [data, setData] = useState<AdminTenantListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchTenants =
    useCallback(async (): Promise<AdminTenantListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenantListResponse>(
          "GET",
          "/api/admin/tenants",
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchTenants().catch(() => {});
  }, [fetchTenants]);

  return {
    data,
    error,
    loading,
    refetch: fetchTenants,
  };
}

// =====================================
// useAdminTenantApi - テナント詳細取得
// =====================================

/** useAdminTenantApiのオプション */
export interface UseAdminTenantApiOptions {
  id: string;
  skip?: boolean;
}

/** useAdminTenantApiの戻り値 */
export interface UseAdminTenantApiResult {
  data: AdminTenantDetailResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminTenantDetailResponse>;
}

/**
 * テナント詳細を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminTenantApiOptions} options オプション
 * @return {UseAdminTenantApiResult} テナント詳細取得結果
 */
export function useAdminTenantApi(
  options: UseAdminTenantApiOptions,
): UseAdminTenantApiResult {
  const [data, setData] = useState<AdminTenantDetailResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchTenant =
    useCallback(async (): Promise<AdminTenantDetailResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenantDetailResponse>(
          "GET",
          `/api/admin/tenants/${options.id}`,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchTenant().catch(() => {});
    }
  }, [options.skip, options.id, fetchTenant]);

  return {
    data,
    error,
    loading,
    refetch: fetchTenant,
  };
}

// =====================================
// useUpdateAdminTenantApi - テナント更新
// =====================================

/** useUpdateAdminTenantApiの戻り値 */
export interface UseUpdateAdminTenantApiResult {
  updateTenant: (id: string, input: UpdateTenantInput) => Promise<AdminTenant>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminTenantApiResult} テナント更新機能
 */
export function useUpdateAdminTenantApi(): UseUpdateAdminTenantApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateTenant = useCallback(
    async (id: string, input: UpdateTenantInput): Promise<AdminTenant> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenant>(
          "PUT",
          `/api/admin/tenants/${id}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateTenant,
    loading,
    error,
  };
}

// =====================================
// useDeleteAdminTenantApi - テナント削除
// =====================================

/** useDeleteAdminTenantApiの戻り値 */
export interface UseDeleteAdminTenantApiResult {
  deleteTenant: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを削除するhook (SYSTEM_ADMIN only)
 * @return {UseDeleteAdminTenantApiResult} テナント削除機能
 */
export function useDeleteAdminTenantApi(): UseDeleteAdminTenantApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteTenant = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchAdminApi<void>("DELETE", `/api/admin/tenants/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    deleteTenant,
    loading,
    error,
  };
}

// =====================================
// useToggleTenantLicenseApi - テナントライセンス切替
// =====================================

/** ライセンス切替レスポンス */
interface TenantLicensesResponse {
  licenses: string[];
}

/** useToggleTenantLicenseApiの戻り値 */
export interface UseToggleTenantLicenseApiResult {
  enableLicense: (tenantId: string, licenseKey: string) => Promise<string[]>;
  disableLicense: (tenantId: string, licenseKey: string) => Promise<string[]>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントライセンスを切り替えるhook (SYSTEM_ADMIN only)
 * @return {UseToggleTenantLicenseApiResult} ライセンス切替機能
 */
export function useToggleTenantLicenseApi(): UseToggleTenantLicenseApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const enableLicense = useCallback(
    async (tenantId: string, licenseKey: string): Promise<string[]> => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await fetchAdminApi<TenantLicensesResponse>(
          "PUT",
          `/api/admin/tenants/${tenantId}/licenses/${licenseKey}`,
        );
        return res.licenses;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const disableLicense = useCallback(
    async (tenantId: string, licenseKey: string): Promise<string[]> => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await fetchAdminApi<TenantLicensesResponse>(
          "DELETE",
          `/api/admin/tenants/${tenantId}/licenses/${licenseKey}`,
        );
        return res.licenses;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    enableLicense,
    disableLicense,
    loading,
    error,
  };
}

// =====================================
// useAdminUsersApi - ユーザー一覧取得
// =====================================

/** useAdminUsersApiのオプション */
export interface UseAdminUsersApiOptions {
  tenantId?: string;
}

/** useAdminUsersApiの戻り値 */
export interface UseAdminUsersApiResult {
  data: AdminUserListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminUserListResponse>;
}

/**
 * ユーザー一覧を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminUsersApiOptions} options オプション
 * @return {UseAdminUsersApiResult} ユーザー一覧取得結果
 */
export function useAdminUsersApi(
  options: UseAdminUsersApiOptions = {},
): UseAdminUsersApiResult {
  const [data, setData] = useState<AdminUserListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async (): Promise<AdminUserListResponse> => {
    setLoading(true);
    setError(undefined);

    try {
      const path = options.tenantId
        ? `/api/admin/users?tenant_id=${options.tenantId}`
        : "/api/admin/users";
      const responseData = await fetchAdminApi<AdminUserListResponse>(
        "GET",
        path,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.tenantId]);

  useEffect(() => {
    fetchUsers().catch(() => {});
  }, [fetchUsers]);

  return {
    data,
    error,
    loading,
    refetch: fetchUsers,
  };
}

// =====================================
// useAdminUserApi - ユーザー詳細取得
// =====================================

/** useAdminUserApiのオプション */
export interface UseAdminUserApiOptions {
  id: string;
  skip?: boolean;
}

/** useAdminUserApiの戻り値 */
export interface UseAdminUserApiResult {
  data: AdminUserDetailResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminUserDetailResponse>;
}

/**
 * ユーザー詳細を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminUserApiOptions} options オプション
 * @return {UseAdminUserApiResult} ユーザー詳細取得結果
 */
export function useAdminUserApi(
  options: UseAdminUserApiOptions,
): UseAdminUserApiResult {
  const [data, setData] = useState<AdminUserDetailResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchUser = useCallback(async (): Promise<AdminUserDetailResponse> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await fetchAdminApi<AdminUserDetailResponse>(
        "GET",
        `/api/admin/users/${options.id}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchUser().catch(() => {});
    }
  }, [options.skip, options.id, fetchUser]);

  return {
    data,
    error,
    loading,
    refetch: fetchUser,
  };
}

// =====================================
// useUpdateAdminUserApi - ユーザー更新
// =====================================

/** useUpdateAdminUserApiの戻り値 */
export interface UseUpdateAdminUserApiResult {
  updateUser: (id: string, input: UpdateUserInput) => Promise<AdminUser>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * ユーザーを更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminUserApiResult} ユーザー更新機能
 */
export function useUpdateAdminUserApi(): UseUpdateAdminUserApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateUser = useCallback(
    async (id: string, input: UpdateUserInput): Promise<AdminUser> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminUser>(
          "PUT",
          `/api/admin/users/${id}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateUser,
    loading,
    error,
  };
}

// =====================================
// useToggleSystemAdminApi - システム管理者権限変更
// =====================================

/** useToggleSystemAdminApiの戻り値 */
export interface UseToggleSystemAdminApiResult {
  toggleSystemAdmin: (id: string, isSystemAdmin: boolean) => Promise<AdminUser>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * システム管理者権限を変更するhook (SYSTEM_ADMIN only)
 * @return {UseToggleSystemAdminApiResult} システム管理者権限変更機能
 */
export function useToggleSystemAdminApi(): UseToggleSystemAdminApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const toggleSystemAdmin = useCallback(
    async (id: string, isSystemAdmin: boolean): Promise<AdminUser> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminUser>(
          "PUT",
          `/api/admin/users/${id}/system-admin`,
          { isSystemAdmin },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    toggleSystemAdmin,
    loading,
    error,
  };
}

// =====================================
// Admin Feature Flag Types
// =====================================

/** テナント override 情報 */
export interface AdminTenantOverride {
  id: string;
  tenantId: string;
  tenantName: string;
  enabled: boolean;
}

/** Feature Flag 情報（Admin用） */
export interface AdminFeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string;
  enabled: boolean;
  overrides: AdminTenantOverride[];
  createdAt: string;
  updatedAt: string;
}

/** Feature Flag 一覧レスポンス */
export interface AdminFeatureFlagListResponse {
  items: AdminFeatureFlag[];
  total: number;
}

// =====================================
// useAdminFeatureFlagsApi - Feature Flag 一覧取得
// =====================================

/** useAdminFeatureFlagsApiの戻り値 */
export interface UseAdminFeatureFlagsApiResult {
  data: AdminFeatureFlagListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminFeatureFlagListResponse>;
}

/**
 * Feature Flag 一覧を取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminFeatureFlagsApiResult} Feature Flag 一覧取得結果
 */
export function useAdminFeatureFlagsApi(): UseAdminFeatureFlagsApiResult {
  const [data, setData] = useState<AdminFeatureFlagListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchFlags =
    useCallback(async (): Promise<AdminFeatureFlagListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlagListResponse>(
          "GET",
          "/api/admin/feature-flags",
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchFlags().catch(() => {});
  }, [fetchFlags]);

  return {
    data,
    error,
    loading,
    refetch: fetchFlags,
  };
}

// =====================================
// useUpdateAdminFeatureFlagApi - Feature Flag 更新
// =====================================

/** useUpdateAdminFeatureFlagApiの戻り値 */
export interface UseUpdateAdminFeatureFlagApiResult {
  updateFlag: (id: string, enabled: boolean) => Promise<AdminFeatureFlag>;
  setTenantOverride: (
    flagId: string,
    tenantId: string,
    enabled: boolean,
  ) => Promise<AdminFeatureFlag>;
  deleteTenantOverride: (
    flagId: string,
    tenantId: string,
  ) => Promise<AdminFeatureFlag>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Feature Flag を更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminFeatureFlagApiResult} Feature Flag 更新機能
 */
export function useUpdateAdminFeatureFlagApi(): UseUpdateAdminFeatureFlagApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateFlag = useCallback(
    async (id: string, enabled: boolean): Promise<AdminFeatureFlag> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlag>(
          "PUT",
          `/api/admin/feature-flags/${id}`,
          { enabled },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const setTenantOverride = useCallback(
    async (
      flagId: string,
      tenantId: string,
      enabled: boolean,
    ): Promise<AdminFeatureFlag> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlag>(
          "PUT",
          `/api/admin/feature-flags/${flagId}/tenants/${tenantId}`,
          { enabled },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteTenantOverride = useCallback(
    async (flagId: string, tenantId: string): Promise<AdminFeatureFlag> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlag>(
          "DELETE",
          `/api/admin/feature-flags/${flagId}/tenants/${tenantId}`,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateFlag,
    setTenantOverride,
    deleteTenantOverride,
    loading,
    error,
  };
}

// =====================================
// Admin Skills Master Types
// =====================================

/** スキルマスター情報（Admin用） */
export interface AdminSkill {
  id: string;
  normalizedName: string;
  displayName: string;
  category: string | null;
  iconUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/** スキルマスター一覧レスポンス */
export interface AdminSkillListResponse {
  totalCount: number;
  nodes: AdminSkill[];
}

/** スキルマスター更新リクエスト */
export interface UpdateSkillInput {
  displayName?: string;
  category?: string | null;
}

// =====================================
// useAdminSkillsApi - スキルマスター一覧取得
// =====================================

/** useAdminSkillsApiのオプション */
export interface UseAdminSkillsApiOptions {
  q?: string;
  category?: string;
  offset?: number;
  limit?: number;
}

/** useAdminSkillsApiの戻り値 */
export interface UseAdminSkillsApiResult {
  data: AdminSkillListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: (
    options?: UseAdminSkillsApiOptions,
  ) => Promise<AdminSkillListResponse>;
}

/**
 * スキルマスター一覧を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminSkillsApiOptions} options オプション
 * @return {UseAdminSkillsApiResult} スキルマスター一覧取得結果
 */
export function useAdminSkillsApi(
  options: UseAdminSkillsApiOptions = {},
): UseAdminSkillsApiResult {
  const [data, setData] = useState<AdminSkillListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Keep latest options in a ref so fetchSkills never depends on them
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const buildPath = useCallback((opts: UseAdminSkillsApiOptions) => {
    const params = new URLSearchParams();
    if (opts.q) params.set("q", opts.q);
    if (opts.category) params.set("category", opts.category);
    if (opts.offset != null) params.set("offset", String(opts.offset));
    if (opts.limit != null) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return qs ? `/api/admin/masters/skills?${qs}` : "/api/admin/masters/skills";
  }, []);

  const fetchSkills = useCallback(
    async (
      fetchOpts?: UseAdminSkillsApiOptions,
    ): Promise<AdminSkillListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminSkillListResponse>(
          "GET",
          buildPath(fetchOpts || optionsRef.current),
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [buildPath],
  );

  // Fetch only once on mount — fetchSkills uses optionsRef so it's stable
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only fetch
  useEffect(() => {
    fetchSkills().catch(() => {});
  }, []);

  return {
    data,
    error,
    loading,
    refetch: fetchSkills,
  };
}

// =====================================
// useUpdateAdminSkillApi - スキルマスター更新
// =====================================

/** useUpdateAdminSkillApiの戻り値 */
export interface UseUpdateAdminSkillApiResult {
  updateSkill: (id: string, input: UpdateSkillInput) => Promise<AdminSkill>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * スキルマスターを更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminSkillApiResult} スキルマスター更新機能
 */
export function useUpdateAdminSkillApi(): UseUpdateAdminSkillApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateSkill = useCallback(
    async (id: string, input: UpdateSkillInput): Promise<AdminSkill> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminSkill>(
          "PUT",
          `/api/admin/masters/skills/${id}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateSkill,
    loading,
    error,
  };
}

// =====================================
// useUploadSkillIconApi - アイコンアップロード
// =====================================

/** useUploadSkillIconApiの戻り値 */
export interface UseUploadSkillIconApiResult {
  uploadIcon: (id: string, file: File) => Promise<AdminSkill>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * スキルマスターのアイコンをアップロードするhook (SYSTEM_ADMIN only)
 * @return {UseUploadSkillIconApiResult} アイコンアップロード機能
 */
export function useUploadSkillIconApi(): UseUploadSkillIconApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const uploadIcon = useCallback(
    async (id: string, file: File): Promise<AdminSkill> => {
      setLoading(true);
      setError(undefined);

      try {
        const token = await getAuthToken();
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(
          `${REST_API_BASE_URL}/api/admin/masters/skills/${id}/icon`,
          {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          },
        );

        if (!res.ok) {
          const errorBody = await res.json().catch(() => ({}));
          throw new Error(
            (errorBody as { detail?: string }).detail ||
              `API error: ${res.status} ${res.statusText}`,
          );
        }

        return res.json();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    uploadIcon,
    loading,
    error,
  };
}

// =====================================
// useDeleteSkillIconApi - アイコン削除
// =====================================

/** useDeleteSkillIconApiの戻り値 */
export interface UseDeleteSkillIconApiResult {
  deleteIcon: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * スキルマスターのアイコンを削除するhook (SYSTEM_ADMIN only)
 * @return {UseDeleteSkillIconApiResult} アイコン削除機能
 */
export function useDeleteSkillIconApi(): UseDeleteSkillIconApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteIcon = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchAdminApi<void>(
        "DELETE",
        `/api/admin/masters/skills/${id}/icon`,
      );
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    deleteIcon,
    loading,
    error,
  };
}
